/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { forwardRef, Fragment, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
// import Table, { TableProps } from 'antd/lib/table';
import { DownOutlined } from '@ant-design/icons';
import { Tree } from 'antd';
import type { TreeDataNode } from 'antd';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { DbFocusOptions } from '~src/types/db';

import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { fileIcons } from '~src/config/fileTypeIcons';
import Icon from '~components/_common/AppIcon/Icon';
import { styleFn } from './styles';

export type PrintTableItem = {
  id?: number;
  name: string;
  description: string | null;
  children?: PrintTableItem[];
  isAttachment: boolean;
  parent_id?: number | null;
};

export interface TendersPrintTableProps /*extends TableProps<PrintTableItem>*/ {
  // highlightedRow?: PrintTableItem;
  focusOptions?: DbFocusOptions[];
  itemsList: PrintTableItem[];
  isListLoading: boolean;
  // expandedRowKeys: string[];
  // onExpand: (expanded: boolean, record: PrintTableItem) => void;
}
/** Default StructuredFoldersTendersDocumentsTable, used with folder navigation */
export const TendersPrintTable = forwardRef<
  HTMLDivElement,
  TendersPrintTableProps
>(
  (
    {
      // highlightedRow,
      focusOptions,
      isListLoading,
      itemsList,
      // expandedRowKeys,
      // onExpand,
    },
    ref
  ) => {
    const styles = useStyles(styleFn);
    const intl = useIntl();

    const archiveSettings = useArchiveSettings();
    const tagsData = archiveSettings.data?.resTags;
    const [fileDescriptions, setFileDescriptions] = useState({});

    // const columns = useMemo<TableProps<PrintTableItem>['columns']>(
    //   () => [
    //     {
    //       title: intl.formatMessage({ id: 'type' }),
    //       dataIndex: 'type',
    //       width: 40,
    //       align: 'center',
    //       render: (type, record) => {
    //         const isTenderFolder = !record.isAttachment;
    //
    //         const nameSections = record.name.split('.');
    //         const extension =
    //           nameSections && nameSections.length
    //             ? nameSections[nameSections.length - 1].toLowerCase()
    //             : undefined;
    //
    //         const attachmentType = (fileIcons[extension || 'default'] ||
    //           fileIcons.default) as typeof fileIcons.docx;
    //
    //         return {
    //           // props: {
    //           //   style: getBackgroundRow(record),
    //           // },
    //           children: (
    //             <div css={styles.iconWrapper}>
    //               <div css={styles.iconContainer}>
    //                 {isTenderFolder ? (
    //                   <NavIcon
    //                     icon={getTenderIconArichive({
    //                       is_sub_folder: !!record.parent_id,
    //                     })}
    //                   />
    //                 ) : (
    //                   <Icon
    //                     icon={[
    //                       attachmentType.iconStyle as IconPrefix,
    //                       attachmentType.icon as IconName,
    //                     ]}
    //                     color={attachmentType.color}
    //                   />
    //                 )}
    //               </div>
    //             </div>
    //           ),
    //         };
    //       },
    //     },
    //     {
    //       title: intl.formatMessage({ id: 'name' }),
    //       dataIndex: 'name',
    //       width: 180,
    //       render: (name, record) => {
    //         return {
    //           // props: {
    //           //   style: getBackgroundRow(record),
    //           // },
    //           children: <span css={styles.tableText}>{name}</span>,
    //         };
    //       },
    //     },
    //     {
    //       title: intl.formatMessage({ id: 'description' }),
    //       dataIndex: 'description',
    //       width: 175,
    //       ellipsis: true,
    //       render: (description, record) => {
    //         return {
    //           // props: {
    //           //   style: getBackgroundRow(record),
    //           // },
    //           children: <span css={styles.tableText}>{description}</span>,
    //         };
    //       },
    //     },
    //   ],
    //   [intl, styles.icons, styles.small, focusOptions, highlightedRow, tagsData]
    // );

    function flattenPrintTableItems(items: PrintTableItem[]): PrintTableItem[] {
      const result: PrintTableItem[] = [];

      function traverse(item: PrintTableItem, parentId: number | null = null) {
        const { children, ...rest } = item;
        result.push({ ...rest, parent_id: parentId });

        if (children) {
          for (const child of children) {
            traverse(child, item.id || null);
          }
        }
      }

      for (const item of items) {
        traverse(item);
      }

      return result;
    }

    function transformListItemToTreeNode(
      item: PrintTableItem,
      key: string
    ): TreeDataNode {
      const isTenderFolder = !item.isAttachment;
      const nameSections = item.name.split('.');
      const extension =
        nameSections && nameSections.length
          ? nameSections[nameSections.length - 1].toLowerCase()
          : undefined;

      const attachmentType = (fileIcons[extension || 'default'] ||
        fileIcons.default) as typeof fileIcons.docx;

      setFileDescriptions((p) => ({ ...p, [key]: item.description }));

      return {
        title: item.name,
        key,
        icon: isTenderFolder ? (
          <NavIcon
            icon={getTenderIconArichive({
              is_sub_folder: !!item.parent_id,
            })}
          />
        ) : (
          <Icon
            icon={[
              attachmentType.iconStyle as IconPrefix,
              attachmentType.icon as IconName,
            ]}
            color={attachmentType.color}
          />
        ),
        children: item.children
          ? item.children.map((child, idx) =>
              transformListItemToTreeNode(child, `${key}-${String(idx)}`)
            )
          : undefined,
      };
    }

    const treeData = useMemo<TreeDataNode[]>(() => {
      return itemsList.map((item, idx) =>
        transformListItemToTreeNode(item, String(idx))
      );
    }, [
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      // highlightedRow,
      tagsData,
      isListLoading,
    ]);

    return (
      <div ref={ref}>
        {isListLoading ? null : (
          // <div css={styles.container}>
          //   <div css={styles.containerTitleWrapper}>
          //     <span css={styles.titleStructure}>
          //       {intl.formatMessage({ id: 'folder-structure' })}
          //     </span>
          //     <span css={styles.titleFS}>
          //       {intl.formatMessage({ id: 'description' })}
          //     </span>
          //   </div>
          //   {/*<div css={styles.containerContentWrapper}>*/}
          <div css={styles.noPointerTree}>
            <Tree
              showIcon
              showLine={{ showLeafIcon: false }}
              defaultExpandAll
              selectable={false}
              switcherIcon={<DownOutlined rev />}
              treeData={treeData}
              titleRender={(node) => (
                <Fragment>
                  <span>
                    {node.title}
                    <span css={styles.contentDescription}>
                      {fileDescriptions[node.key]}
                    </span>
                  </span>
                </Fragment>
              )}
            />
          </div>
          //   {/*<div css={styles.contentSizesWrapper}>*/}
          //   {/*  {treeDescriptions.map((size) => (*/}
          //   {/*    <span css={styles.contentSize} key={size}>*/}
          //   {/*      {size}*/}
          //   {/*    </span>*/}
          //   {/*  ))}*/}
          //   {/*</div>*/}
          //   {/*</div>*/}
          // </div>
        )}
        {/*<Table<PrintTableItem>*/}
        {/*  // scroll={{ y: '50vh', x: 1090 }}*/}
        {/*  css={styles.printTable}*/}
        {/*  rowClassName="table-narrow-row"*/}
        {/*  rowKey={(record) => String(record.id)}*/}
        {/*  showSorterTooltip={false}*/}
        {/*  bordered*/}
        {/*  pagination={false}*/}
        {/*  expandable={{ expandedRowKeys, onExpand }}*/}
        {/*  loading={isListLoading}*/}
        {/*  columns={columns}*/}
        {/*  dataSource={itemsList}*/}
        {/*/>*/}
      </div>
    );
  }
);
